/**
 * Supabase Compatibility Layer
 * 
 * This module provides a drop-in replacement for the Supabase client
 * that routes all calls through the new API backend.
 * 
 * Usage:
 *   import { supabase } from '@/lib/supabase-compat'
 * 
 * This allows existing code to work without changes while we gradually
 * migrate to direct API calls.
 */

import { cerrarSesionPorExpiracion, esSesionVencida } from './sesion'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

// Route mapping: tables that don't map directly to /api/{table}
const TABLE_ROUTES: Record<string, string> = {
  'conversaciones': '/api/chat/conversaciones',
  'mensajes': '/api/chat/mensajes',
  'mensajes_programados': '/api/chat/mensajes-programados',
  'perfiles_equipo': '/api/config/perfiles-equipo',
  'config_detraccion': '/api/config/detraccion',
  'config_tarifa_cliente': '/api/config/tarifa-cliente',
  'portal_accesos': '/api/config/portal-accesos',
  'agente_config': '/api/agentes/config',
  'kb_articulos': '/api/agentes/kb',
  'agente_kb_articulos': '/api/agentes/kb-assignments',
  'agente_memoria': '/api/agentes/memoria',
  'agente_worker_skills': '/api/agentes/skills',
  'social_strategy': '/api/social/strategy',
  'social_posts': '/api/social/posts',
  'social_ai_conversations': '/api/social/ai-conversations',
  'centrales_costo': '/api/contabilidad/centrales-costo',
  'costos_estimados': '/api/contabilidad/costos-estimados',
  'asignaciones_central': '/api/contabilidad/asignaciones-central',
  'movimientos_caja': '/api/contabilidad/movimientos',
  'pagos_programados': '/api/contabilidad/pagos-programados',
  'pagos_recurrentes': '/api/contabilidad/pagos-recurrentes',
  'gastos_proyecto': '/api/contabilidad/gastos-proyecto',
  'push_subscriptions': '/api/config/push-subscriptions',
  'chat_artifacts': '/api/social/artifacts',
  'wiki_categorias': '/api/wiki/categorias',
  'wiki_articulos': '/api/wiki/articulos',
  'wiki_anotaciones': '/api/wiki/anotaciones',
  'wiki_comentarios': '/api/wiki/comentarios',
  'wiki_versiones': '/api/wiki/versiones',
}

function getApiPath(table: string): string {
  return TABLE_ROUTES[table] || `/api/${table}`
}

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function apiRequest(path: string, options: RequestInit = {}): Promise<any> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...getHeaders(),
      ...((options.headers as Record<string, string>) || {}),
    },
  })
  
  if (!res.ok) {
    // Sesión vencida: al login, no a un error genérico por pantalla.
    if (esSesionVencida(res.status)) {
      cerrarSesionPorExpiracion()
      throw new Error('Tu sesion expiro')
    }
    const error = await res.json().catch(() => ({ error: 'Error de red' }))
    throw new Error(error.error || `HTTP ${res.status}`)
  }

  return res.json()
}

// Query builder that mimics Supabase's fluent API
class QueryBuilder {
  private table: string
  private queryParams: URLSearchParams = new URLSearchParams()
  private method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET'
  private body: any = null
  private selectFields: string = '*'
  private isCount: boolean = false

  constructor(table: string) {
    this.table = table
  }

  select(fields: string = '*', options?: { count?: string; head?: boolean }): this {
    this.selectFields = fields
    if (options?.count) {
      this.isCount = true
    }
    return this
  }

  insert(data: any): this {
    this.method = 'POST'
    this.body = data
    return this
  }

  upsert(data: any, _options?: { onConflict?: string }): this {
    this.method = 'PUT'
    this.body = data
    this.queryParams.set('upsert', 'true')
    return this
  }

  update(data: any): this {
    this.method = 'PUT'
    this.body = data
    return this
  }

  delete(): this {
    this.method = 'DELETE'
    return this
  }

  eq(field: string, value: any): this {
    this.queryParams.set(field, String(value))
    return this
  }

  neq(field: string, value: any): this {
    this.queryParams.set(`${field}_neq`, String(value))
    return this
  }

  lte(field: string, value: any): this {
    this.queryParams.set(`${field}_lte`, String(value))
    return this
  }

  gte(field: string, value: any): this {
    this.queryParams.set(`${field}_gte`, String(value))
    return this
  }

  lt(field: string, value: any): this {
    this.queryParams.set(`${field}_lt`, String(value))
    return this
  }

  gt(field: string, value: any): this {
    this.queryParams.set(`${field}_gt`, String(value))
    return this
  }

  like(field: string, value: string): this {
    this.queryParams.set(`${field}_like`, value)
    return this
  }

  ilike(field: string, value: string): this {
    this.queryParams.set(`${field}_ilike`, value)
    return this
  }

  or(condition: string): this {
    this.queryParams.set('or', condition)
    return this
  }

  not(field: string, operator: string, value: any): this {
    this.queryParams.set(`${field}_not_${operator}`, String(value))
    return this
  }

  in(field: string, values: any[]): this {
    this.queryParams.set(field, values.join(','))
    return this
  }

  order(field: string, options?: { ascending?: boolean; nullsFirst?: boolean }): this {
    this.queryParams.set('sort', field)
    // Always send order param — default to 'asc' (matches Supabase behavior)
    this.queryParams.set('order', options?.ascending === false ? 'desc' : 'asc')
    if (options?.nullsFirst === false) {
      this.queryParams.set('nullsFirst', 'false')
    }
    return this
  }

  limit(count: number): this {
    this.queryParams.set('limit', String(count))
    return this
  }

  single(): this {
    this.queryParams.set('single', 'true')
    return this
  }

  maybeSingle(): this {
    this.queryParams.set('single', 'true')
    return this
  }

  async then(resolve: (result: { data: any; error: any; count?: number }) => void, _reject?: (error: any) => void) {
    try {
      // Check if this is a getById request (eq('id', value) + single())
      const idValue = this.queryParams.get('id')
      const isSingle = this.queryParams.get('single') === 'true'
      const isGetById = this.method === 'GET' && idValue && isSingle

      const basePath = getApiPath(this.table)
      let url = basePath
      
      if (isGetById) {
        // Use path parameter for getById: /api/{table}/{id}
        url = `${basePath}/${idValue}`
        // Remove id and single from query params
        this.queryParams.delete('id')
        this.queryParams.delete('single')
      }

      if (this.isCount) {
        url += '/count'
      }
      
      const queryString = this.queryParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }

      let result: any

      if (this.method === 'GET') {
        if (this.isCount) {
          // For count queries, return the count
          result = await apiRequest(url)
          resolve({ data: null, error: null, count: result.count || 0 })
          return
        }
        result = await apiRequest(url)
      } else if (this.method === 'POST') {
        result = await apiRequest(url, {
          method: 'POST',
          body: JSON.stringify(this.body),
        })
      } else if (this.method === 'PUT') {
        // For update with eq, extract the ID
        const id = this.queryParams.get('id')
        if (id) {
          result = await apiRequest(`${basePath}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(this.body),
          })
        } else {
          result = await apiRequest(url, {
            method: 'PUT',
            body: JSON.stringify(this.body),
          })
        }
      } else if (this.method === 'DELETE') {
        const id = this.queryParams.get('id')
        if (id) {
          result = await apiRequest(`${basePath}/${id}`, { method: 'DELETE' })
        } else {
          result = await apiRequest(url, { method: 'DELETE' })
        }
      }

      // Handle single result (for non-getById cases)
      if (isSingle && !isGetById && Array.isArray(result)) {
        result = result[0] || null
      }

      // For getById, result is already a single object

      // For insert, return the created record with id
      if (this.method === 'POST' && !this.selectFields.includes('id')) {
        resolve({ data: result, error: null })
      } else {
        resolve({ data: result, error: null })
      }
    } catch (error: any) {
      resolve({ data: null, error })
    }
  }
}

// Storage compatibility
class StorageBucket {
  private bucket: string

  constructor(bucket: string) {
    this.bucket = bucket
  }

  async upload(path: string, file: File | Blob, _options?: { contentType?: string; upsert?: boolean }) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('bucket', this.bucket)
      formData.append('path', path)

      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_URL}/api/storage/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      })

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Upload failed' }))
        return { error }
      }

      const data = await res.json()
      return { data, error: null }
    } catch (error: any) {
      return { data: null, error }
    }
  }

  getPublicUrl(path: string) {
    return {
      data: {
        publicUrl: `${API_URL}/api/storage/${this.bucket}/${path}`,
      },
    }
  }

  async remove(paths: string[]) {
    try {
      for (const path of paths) {
        await apiRequest(`/api/storage/${this.bucket}/${path}`, { method: 'DELETE' })
      }
      return { data: null, error: null }
    } catch (error: any) {
      return { data: null, error }
    }
  }
}

// Functions compatibility
class FunctionsClient {
  private static FUNCTION_ROUTES: Record<string, string> = {
    'chat-send': '/api/chat/send',
    'wazzup-send': '/api/chat/send-first',
    'chat-schedule-message': '/api/chat/schedule',
    'chat-cancel-scheduled': '/api/chat/cancel-scheduled',
    'register-client-user': '/api/auth/register',
    'google-auth-url': '/api/google/auth-url',
    'google-create-doc': '/api/google/create-doc',
    'google-refresh-token': '/api/google/refresh-token',
    'gemini-validate-doc': '/api/google/validate-doc',
    'kb-embed': '/api/agentes/kb/embed',
    'memory-extract': '/api/agentes/memoria/extract',
  }

  async invoke(name: string, options?: { body?: any }) {
    try {
      const path = FunctionsClient.FUNCTION_ROUTES[name] || `/api/functions/${name}`
      const result = await apiRequest(path, {
        method: 'POST',
        body: options?.body ? JSON.stringify(options.body) : undefined,
      })
      return { data: result, error: null }
    } catch (error: any) {
      return { data: null, error }
    }
  }
}

// Auth compatibility
class AuthClient {
  async getSession() {
    const token = localStorage.getItem('auth_token')
    return {
      data: {
        session: token ? { access_token: token } : null,
      },
    }
  }

  async signInWithPassword({ email, password }: { email: string; password: string }) {
    try {
      const result = await apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })
      localStorage.setItem('auth_token', result.token)
      return { data: { session: { access_token: result.token }, user: result.user }, error: null }
    } catch (error: any) {
      return { data: null, error }
    }
  }

  async signOut() {
    localStorage.removeItem('auth_token')
    return { error: null }
  }

  async updateUser({ password, data: _data }: { password?: string; data?: any }) {
    try {
      await apiRequest('/api/auth/password', {
        method: 'PUT',
        body: JSON.stringify({ password }),
      })
      return { error: null }
    } catch (error: any) {
      return { error }
    }
  }

  onAuthStateChange(callback: (event: string, session: any) => void) {
    // Simple implementation - just call once with current state
    const token = localStorage.getItem('auth_token')
    if (token) {
      callback('SIGNED_IN', { access_token: token })
    } else {
      callback('SIGNED_OUT', null)
    }

    return {
      data: {
        subscription: {
          unsubscribe: () => {},
        },
      },
    }
  }
}

// Main Supabase-compatible client
export const supabase = {
  from(table: string) {
    return new QueryBuilder(table)
  },

  storage: {
    from(bucket: string) {
      return new StorageBucket(bucket)
    },
  },

  functions: new FunctionsClient(),

  auth: new AuthClient(),

  // Channel stub — Supabase Realtime replaced with SSE
  // Realtime events flow through GET /api/realtime/events (Server-Sent Events)
  // The singleton realtime client in src/lib/realtime-events.ts handles the connection.
  // Use useRealtimeEvent() hook instead of supabase.channel().
  channel(_name: string, _options?: any) {
    return {
      on(_event: string, _filter: any, _callback?: any) { return this },
      subscribe(_callback?: any) { return this },
      unsubscribe() {},
    }
  },

  removeChannel(_channel: any) {},
}
